import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Award,
  Download,
  GraduationCap,
} from 'lucide-react';
import { useMyEnrollments } from '@/hooks/useCourses';
import { formatDateShort } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

export default function Certificates() {
  const { t } = useTranslation('account');
  const { data, isLoading, isError } = useMyEnrollments();

  const enrollments = data?.enrollments ?? [];
  const completedEnrollments = enrollments.filter(
    (e: any) => e.status === 'completed' && e.certificate_number
  );

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">{t('certificates.errorLoading')}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  if (completedEnrollments.length === 0) {
    return (
      <div className="text-center py-16">
        <Award
          className="h-16 w-16 text-ink-muted mx-auto mb-4"
          aria-hidden="true"
        />
        <h2 className="text-xl font-display font-bold text-ink mb-2">
          {t('certificates.emptyHeading')}
        </h2>
        <p className="text-ink-muted mb-6">{t('certificates.emptyDescription')}</p>
        <Link to="/account/courses">
          <Button>{t('certificates.goToCourses')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('certificates.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('certificates.totalCount', { count: completedEnrollments.length })}
        </p>
      </div>

      <div className="space-y-4">
        {completedEnrollments.map((enrollment: any) => (
          <Card key={enrollment.id}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-100 shrink-0">
                  <GraduationCap
                    className="h-6 w-6 text-amber-700"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-ink">
                    {enrollment.course?.title ?? t('certificates.untitled')}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-ink-muted">
                    <span>
                      {t('certificates.certificateNumber', { number: enrollment.certificate_number })}
                    </span>
                    {enrollment.completed_at && (
                      <span>
                        {t('certificates.issuedOn', { date: formatDateShort(enrollment.completed_at) })}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <a
                href={`/api/my-courses/${enrollment.course_id}/certificate/download`}
                download
                className="shrink-0"
              >
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {t('certificates.downloadPdf')}
                </Button>
              </a>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
